import { useContext, useState, useTransition } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Avatar from '@givebutter/primitives/avatar';
import { Box } from '@givebutter/primitives/box';
import { ChartTypes, LineChart, TooltipContext } from '@givebutter/primitives/chart';
import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal } from '@givebutter/util';
import { faArrowUp, faChevronDown, faHeartHalfStroke } from '@fortawesome/pro-regular-svg-icons';
import { faRepeat, faSackDollar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchContactGivingSummaryChartStats } from 'api/contacts/contacts';
import * as DropdownMenu from 'components/dropdown-menu';
import { EmptyData } from 'components/util';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import useDebounce from 'customHooks/useDebounce';
import { rawDateFromServer } from 'helpers/dates';
import { transformCurrencyToCompactNumberFormat } from 'helpers/formatting';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { first } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { twJoin } from 'tailwind-merge';
import { Transaction } from 'types/domain';

interface ChartShape {
    date: string;
    Amount: number;
    transaction: Transaction;
}

enum DateOption {
    LastYear = 'last_year',
    YearToDate = 'year_to_date',
    AllTime = 'all_time',
}

type DateOptions = {
    [key in DateOption]: {
        type: DateOption;
        buttonLabel: string;
        emptyLabel: string;
        topDonorLabel: string;
    };
};

const ContactSidebarGivingSummaryChart = () => {
    const [isPendingInternal, startTransition] = useTransition();
    const isPending = useDebounce(isPendingInternal, 500);
    const { accountId } = useCurrentAccount();
    const { data: contact } = useCurrentContact();
    const currencySymbol = useCurrencySymbol();
    const { transactionSoftCredits = false } = useFlags();

    const dateOptions: DateOptions = {
        [DateOption.YearToDate]: {
            type: DateOption.YearToDate,
            buttonLabel: 'Year to date',
            emptyLabel: 'This contact has not given this year.',
            topDonorLabel: 'of donors this year',
        },
        [DateOption.LastYear]: {
            type: DateOption.LastYear,
            buttonLabel: 'Last year',
            emptyLabel: 'This contact did not give last year.',
            topDonorLabel: 'of donors last year',
        },
        [DateOption.AllTime]: {
            type: DateOption.AllTime,
            buttonLabel: 'All time',
            emptyLabel: 'This contact has yet to give.',
            topDonorLabel: 'of all donors',
        },
    };

    const [selectedDateOption, setSelectedDateOption] = useState<DateOption>(DateOption.YearToDate);

    const {
        data = {
            chartData: [],
            total: 0,
            transaction_ids: [],
            top_donor: false,
            donor_percentile: 99,
            largest_donation: null,
            recurring_donation: null,
            recurring_donations_sum: 0,
            soft_credits_sum: 0,
        },
    } = useSuspenseQuery({
        queryKey: accountQueryKeys.contactGivingSummaryChart(
            accountId,
            contact.id,
            selectedDateOption,
            contact.type,
        ),
        queryFn: () => {
            return fetchContactGivingSummaryChartStats(
                accountId,
                contact.id,
                dateOptions[selectedDateOption].type,
                contact.type,
            );
        },
        refetchOnWindowFocus: false,
        select: (
            data,
        ): {
            chartData: ChartTypes.ChartData<ChartShape>;
            total: number;
            top_donor: boolean;
            donor_percentile: number;
            largest_donation: null | Transaction;
            recurring_donation: null | Transaction;
            recurring_donations_sum: number;
            soft_credits_sum: number;
        } => {
            const chartData = data.transactions.map(transaction => {
                return {
                    date: rawDateFromServer(transaction.transacted_at).toLocaleString(
                        DateTime.DATE_MED,
                    ),
                    transaction: transaction,
                    Amount: transaction.amount,
                };
            });

            return {
                chartData,
                total: data.aggregates_by_date_range.total,
                top_donor: data.aggregates_by_date_range.top_donor,
                largest_donation: data.aggregates_by_date_range.largest_donation,
                recurring_donation: data.aggregates_by_date_range.recurring_donation,
                recurring_donations_sum: data.aggregates_by_date_range.recurring_donations_sum,
                soft_credits_sum: data.aggregates_by_date_range.soft_credits_sum,
                donor_percentile:
                    data.aggregates_by_date_range.donor_percentile === 0
                        ? 1
                        : data.aggregates_by_date_range.donor_percentile,
            };
        },
    });

    const {
        chartData,
        soft_credits_sum,
        largest_donation,
        total,
        top_donor,
        recurring_donation,
        recurring_donations_sum,
        donor_percentile,
    } = data;

    const hasSoftCredits = transactionSoftCredits && !!soft_credits_sum;
    const didGive = total > 0;

    return (
        <Box className={twJoin(isPending && 'is-loading')}>
            <div className="px-4 py-3 text-base font-medium text-gray-900">Giving Summary</div>
            <Separator />
            <div className="flex flex-col gap-1 p-4">
                <div className="flex items-center justify-between gap-2">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger>
                            <div className="flex justify-between">
                                <div className="whitespace-nowrap text-sm text-gray-600">
                                    {dateOptions[selectedDateOption].buttonLabel}&nbsp;
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </div>
                            </div>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Content align={'start'} sideOffset={4}>
                            {Object.values(DateOption).map(val => (
                                <DropdownMenu.Item
                                    key={val}
                                    onClick={() =>
                                        startTransition(() => setSelectedDateOption(val))
                                    }
                                >
                                    {dateOptions[val].buttonLabel}
                                </DropdownMenu.Item>
                            ))}
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                    {top_donor && (
                        <div className="text-right text-xs font-medium text-green-500">
                            <FontAwesomeIcon icon={faArrowUp} /> Top {donor_percentile}%{' '}
                            {dateOptions[selectedDateOption].topDonorLabel}
                        </div>
                    )}
                </div>
                {didGive && (
                    <div className="text-base font-medium text-gray-900">
                        {currencySymbol}
                        {maskDecimal(total)}
                    </div>
                )}
                {didGive && (
                    <LineChart.Root
                        data={chartData}
                        index={'date'}
                        categories={['Amount']}
                        yAxisWidth={58}
                        valueFormatter={value => `${currencySymbol}${maskDecimal(value)}`}
                        tickFormatter={(_, rawValue) => {
                            if (rawValue >= 999) {
                                const formatted = transformCurrencyToCompactNumberFormat(rawValue);
                                return `${currencySymbol}${formatted}`;
                            }

                            return `${currencySymbol}${maskDecimal(rawValue)}`;
                        }}
                    >
                        <LineChart.Content className={'h-36'}>
                            <LineChart.Tooltip
                                offset={-60}
                                position={{ y: 20 }}
                                allowEscapeViewBox={{
                                    x: true,
                                }}
                            >
                                <CustomTooltip />
                            </LineChart.Tooltip>
                        </LineChart.Content>
                    </LineChart.Root>
                )}
                {!didGive && (
                    <div className="mt-1 flex flex-col items-center justify-center bg-gray-50 px-4 py-4 text-center">
                        <p className="text-sm text-gray-600">
                            {dateOptions[selectedDateOption].emptyLabel}
                        </p>
                    </div>
                )}
            </div>
            <div className="flex flex-col border-t border-gray-200">
                <div className="flex justify-between gap-2">
                    <div className="flex flex-1 justify-start px-4 py-3">
                        <Avatar.Root className={'!h-5 !w-5 bg-gray-100'}>
                            <Avatar.Fallback aria-label="Dollar sign icon">
                                <FontAwesomeIcon
                                    size="2xs"
                                    className="text-gray-600"
                                    icon={faSackDollar}
                                />
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <div className="group flex-1">
                            <div className="text-sm text-gray-600">Largest</div>
                            <div>
                                {largest_donation ? (
                                    <div className="flex flex-wrap justify-between">
                                        <div className="text-base font-medium text-gray-900">
                                            {currencySymbol}
                                            {maskDecimal(largest_donation.amount)}
                                        </div>
                                        <Link
                                            to={`/accounts/${accountId}/contacts/${contact.id}/transactions/${largest_donation.id}`}
                                            className="hidden text-sm font-medium text-blue-500 group-hover:block"
                                        >
                                            View
                                        </Link>
                                    </div>
                                ) : (
                                    <EmptyData />
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-1 justify-start gap-2 px-4 py-3">
                        <Avatar.Root className={'!h-5 !w-5 bg-gray-100'}>
                            <Avatar.Fallback aria-label="Recurring icon">
                                <FontAwesomeIcon
                                    size="2xs"
                                    className="text-gray-600"
                                    icon={faRepeat}
                                />
                            </Avatar.Fallback>
                        </Avatar.Root>
                        <div className="group flex-1">
                            <div className="text-sm text-gray-600">Recurring</div>
                            <div>
                                {!!recurring_donations_sum && !!recurring_donation ? (
                                    <div className="flex flex-wrap justify-between">
                                        <div className="text-base font-medium text-gray-900">
                                            {currencySymbol}
                                            {maskDecimal(recurring_donations_sum)}
                                        </div>
                                        <Link
                                            to={`/accounts/${accountId}/plans/${recurring_donation.plan_id}`}
                                            className="hidden text-sm font-medium text-blue-500 group-hover:block"
                                        >
                                            View
                                        </Link>
                                    </div>
                                ) : (
                                    <EmptyData />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex justify-between gap-2">
                    {hasSoftCredits && (
                        <div className="flex flex-1 justify-start gap-2 px-4 py-3">
                            <Avatar.Root className={'!h-5 !w-5 bg-gray-100'}>
                                <Avatar.Fallback aria-label="Soft credit icon">
                                    <FontAwesomeIcon
                                        size="2xs"
                                        className="text-gray-600"
                                        icon={faHeartHalfStroke}
                                    />
                                </Avatar.Fallback>
                            </Avatar.Root>
                            <div className="group flex-1">
                                <div className="text-sm text-gray-600">Soft credits</div>
                                <div>
                                    <div className="text-base font-medium text-gray-900">
                                        {currencySymbol}
                                        {maskDecimal(soft_credits_sum)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Box>
    );
};

const CustomTooltip = () => {
    const match = useRouteMatch();
    const currencySymbol = useCurrencySymbol();
    const { payload: _payload } = useContext(TooltipContext.TooltipContext);
    const payload = first(_payload);

    if (!payload) {
        return null;
    }

    const { date, Amount, transaction } = payload.payload as ChartShape;

    return (
        <Box className={'pointer-events-auto flex flex-col gap-1 p-2 text-xs'}>
            <p>{date}</p>
            <p className={'flex items-center gap-1'}>
                <span className={'font-medium'}>
                    {currencySymbol}
                    {maskDecimal(Amount)}
                </span>
                <Link
                    className={'text-gray-900 underline'}
                    to={`${match.url}/transactions/${transaction.id}`}
                >
                    View
                </Link>
            </p>
        </Box>
    );
};

export { ContactSidebarGivingSummaryChart, DateOption };
